import { FetchError, fetchJson } from './client';
import type { League } from '../league/league';
import {
    parsePlayerNews, parsePlayers, parsePoints, parseScores, parseStandings, parseUpdateId, parseVersionInfo,
    parseWhatsNew,
} from '../model/parse';
import type { Division, Game, Player, PlayerNews, PointsByDate, VersionInfo, WhatsNewItem } from '../model/types';

const VERSION_TIMEOUT_MS = 8000;
const NEWS_TIMEOUT_MS = 8000;
const WHATS_NEW_TIMEOUT_MS = 8000;

// Backend address: set per mode in .env.development / .env.production.
export const apiBase = (): string => import.meta.env.VITE_API_BASE as string;

const ESPN_NEWS_URL = 'https://site.api.espn.com/apis/fantasy/v2/games/fhl/news/players';

// Everything the app asks the network for. The sync engine and the news loader
// take this as a parameter so tests can replace it.
export interface Api {
    fetchVersionInfo(signal?: AbortSignal): Promise<VersionInfo>;
    fetchPlayers(country: string, signal?: AbortSignal): Promise<Player[]>;
    fetchPoints(country: string, signal?: AbortSignal): Promise<PointsByDate>;
    fetchScores(signal?: AbortSignal): Promise<Game[]>;
    fetchStandings(signal?: AbortSignal): Promise<Division[]>;
    fetchAllPlayers(signal?: AbortSignal): Promise<Player[]>;
    // The previous game day's games and the country's players who played in
    // them, as the backend saved them at the game day switch.
    fetchPreviousDayScores(signal?: AbortSignal): Promise<Game[]>;
    fetchPreviousDayPlayers(country: string, signal?: AbortSignal): Promise<Player[]>;
    fetchPlayerNews(espnId: number, signal?: AbortSignal): Promise<PlayerNews | undefined>;
}

// The league's data is switched off on the backend (/api/ahl/* answers 404).
export class LeagueUnavailableError extends Error {
    constructor(league: League) {
        super(`${league} data is not available`);
        this.name = 'LeagueUnavailableError';
    }
}

// The NHL data.
export const api: Api = {
    async fetchVersionInfo(signal) {
        return parseVersionInfo(await fetchJson(`${apiBase()}/api/versionInfo`, { signal, timeoutMs: VERSION_TIMEOUT_MS }));
    },
    async fetchPlayers(country, signal) {
        return parsePlayers(await fetchJson(`${apiBase()}/api/players/${country}`, { signal }));
    },
    async fetchPoints(country, signal) {
        return parsePoints(await fetchJson(`${apiBase()}/api/points/${country}`, { signal }));
    },
    async fetchScores(signal) {
        return parseScores(await fetchJson(`${apiBase()}/api/scores`, { signal }));
    },
    async fetchStandings(signal) {
        return parseStandings(await fetchJson(`${apiBase()}/api/standings`, { signal }));
    },
    async fetchAllPlayers(signal) {
        return parsePlayers(await fetchJson(`${apiBase()}/api/allPlayers`, { signal }));
    },
    async fetchPreviousDayScores(signal) {
        return parseScores(await fetchJson(`${apiBase()}/api/previousDay/scores`, { signal }));
    },
    async fetchPreviousDayPlayers(country, signal) {
        return parsePlayers(await fetchJson(`${apiBase()}/api/previousDay/players/${country}`, { signal }));
    },
    async fetchPlayerNews(espnId, signal) {
        const url = `${ESPN_NEWS_URL}?days=100&limit=2&playerId=${espnId}`;
        return parsePlayerNews(await fetchJson(url, { signal, timeoutMs: NEWS_TIMEOUT_MS }));
    },
};

const ahlUpdateIdUrl = () => `${apiBase()}/api/ahl/updateId`;

// The AHL data: the same shapes under /api/ahl, with its own updateId. There is
// no points-per-day data and no app version, so the app version still comes
// from /api/versionInfo. ESPN news and the previous game day are NHL only and
// are not shown for the AHL.
export const ahlApi: Api = {
    async fetchVersionInfo(signal) {
        const [version, ahl] = await Promise.all([
            api.fetchVersionInfo(signal),
            fetchJson(ahlUpdateIdUrl(), { signal, timeoutMs: VERSION_TIMEOUT_MS }).catch((error: unknown) => {
                throw error instanceof FetchError && error.status === 404 ? new LeagueUnavailableError('AHL') : error;
            }),
        ]);
        return { ...parseUpdateId(ahl), appVersion: version.appVersion };
    },
    async fetchPlayers(country, signal) {
        return parsePlayers(await fetchJson(`${apiBase()}/api/ahl/players/${country}`, { signal }));
    },
    async fetchPoints() {
        return {};
    },
    async fetchScores(signal) {
        return parseScores(await fetchJson(`${apiBase()}/api/ahl/scores`, { signal }));
    },
    async fetchStandings(signal) {
        return parseStandings(await fetchJson(`${apiBase()}/api/ahl/standings`, { signal }));
    },
    async fetchAllPlayers(signal) {
        return parsePlayers(await fetchJson(`${apiBase()}/api/ahl/allPlayers`, { signal }));
    },
    async fetchPreviousDayScores() {
        return [];
    },
    async fetchPreviousDayPlayers() {
        return [];
    },
    fetchPlayerNews: api.fetchPlayerNews,
};

export const apiFor = (league: League): Api => (league === 'AHL' ? ahlApi : api);

// True when the backend serves AHL data (used to offer the AHL in the menu).
export async function isAhlAvailable(signal?: AbortSignal): Promise<boolean> {
    try {
        await fetchJson(ahlUpdateIdUrl(), { signal, timeoutMs: VERSION_TIMEOUT_MS });
        return true;
    } catch (error) {
        return !(error instanceof FetchError && error.status === 404);
    }
}

// The app's "What's new" list, the same for both leagues. It only changes when
// the backend is deployed, so it is not tied to the updateId.
export async function fetchWhatsNew(signal?: AbortSignal): Promise<WhatsNewItem[]> {
    return parseWhatsNew(await fetchJson(`${apiBase()}/api/whatsNew`, { signal, timeoutMs: WHATS_NEW_TIMEOUT_MS }));
}
